/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  Button,
  Grid,
  Icon,
  IconButton,
  List,
  Modal,
  Placeholder,
  Spacer,
  Table,
  TextGroupMedium,
} from "@cd2/components/build/es6"
import React, { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"
import { CreateButton } from "../../Components/Buttons/CreateButton"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { useConfirmation } from "../../hooks/useConfirmation"
import { Fields, Form2 } from "@cd2/forms"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import {
  EOS_SKEEM_TOKEN,
  setLocalStorageWithExpiry,
  useGlobalState,
} from "../../Components/GlobalStateProvider"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { assert, unique } from "@cd2/tools/build/es6"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { brandColors, colors, themeColors } from "../../Components/EOSThemeProvider"
import { icons } from "../../constants/icons"
import { differenceInSeconds, formatDistanceToNowStrict, sub } from "date-fns"
import { buildRoute, eRoutes } from "../../routes"
import { UserSelectList } from "../../Components/SelectLists/UserSelectList"
import { CancelButton } from "../../Components/Buttons/CancelButton"
import { useFormSubmitLock } from "@cd2/forms/build/es6/Form2"
import { isBlank } from "@cd2/components"
import { FieldWithDescription } from "../../Components/FieldWithDescription"
import { DropdownMenu } from "../../Components/DropdownMenu"
import { H4, P } from "@cd2/text"
import { useBlockUserToggle } from "../../hooks/useBlockUserToggle"
import { MenuColumn } from "../../Components/Tables/utils"
import { ContactSelectList } from "../../Components/SelectLists/ContactSelectList"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { ToggleSwitch } from "../../Components/ToggleSwitch"
import { useConfirmDelete } from "../../hooks/useConfirmCancelConfirmation"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { pluralize } from "@cd2/tools"
import { QuickToolTipWrapper } from "../../Components/QuickToolTipWrapper"
import { startBranchGuestSession } from "../../utils/branchGuestSession"

type ICustomer = IRecord<"customers", "name" | "isGenericCustomer"> & {
  contacts: (IRecord<
    "contacts",
    | "job_title"
    | "name"
    | "first_name"
    | "last_name"
    | "email"
    | "phone"
    | "sage_id"
    | "receivesMonthlyPriceList"
    | "default_delivery_note"
    | "is_default"
    | "useForMsEmailEventFetch"
    | "marketingAccepted"
  > & {
    user: IRecord<
      "skeem_users",
      | "name"
      | "skeem_email"
      | "last_seen"
      | "isInvited"
      | "isBlocked"
      | "customerIsPrimaryAccountHolder"
      | "phoneNumber"
      | "marketingAccepted"
      | "customerAreaQuotesOnly"
    >
    quotes: IRecord<"quotes", "created_at">[]
    orders: IRecord<"orders", "created_at">[]
  })[]
}

export function CustomerShowContactsPage() {
  const { id } = useParams<{ id: string }>()
  const skeem = window.skeem
  const alerts = useSiteAlerts()
  const { currentUser } = useGlobalState()
  const [customer, setCustomer] = React.useState<ICustomer | null>(null)
  const [contactQuery, setContactQuery] = React.useState<string>("")
  const [contactFormNode, getContactInformation, handleCreate] =
    useContactCreateForCustomerConfirmation()
  const [passwordNode, getPassword] = useEditUserConfirmation()
  const [deleteContactNode, confirmDeleteContact] = useConfirmDelete()
  const [confirmDeleteWithAccountNode, handleConfirmDeleteWithAccount] = useConfirmation(
    ({ onResolve }) => (
      <ConfirmCancelDialogue
        heading={"Are you sure you want to delete this contact?"}
        body={
          <React.Fragment>
            <P>This contact is linked to a user account</P>

            <P>
              Both contact and user account will be <b>permanently</b> deleted and cannot be undone,{" "}
              <b>are you sure you want to proceed?</b>
            </P>
          </React.Fragment>
        }
        onConfirm={() => onResolve(true)}
        onCancel={() => onResolve(false)}
        buttonOpts={{ confirm: { children: "Delete", buttonColor: brandColors.blaze } }}
        isOpen
      />
    ),
  )
  const [confirmDeleteUserNode, handleConfirmDelete] = useConfirmation(({ onResolve }) => (
    <ConfirmCancelDialogue
      heading={"Are you sure you want to delete this user?"}
      body={
        <React.Fragment>
          <P>This cannot be undone</P>
        </React.Fragment>
      }
      onConfirm={() => onResolve(true)}
      onCancel={() => onResolve(false)}
      buttonOpts={{ confirm: { children: "Delete", buttonColor: brandColors.blaze } }}
      isOpen
    />
  ))

  const handleSignInAs = useAsyncCallback(async (user: { id: string }) => {
    const skeemAuthToken = await skeem.getAuthToken()
    if (!skeemAuthToken) {
      alerts.genericError("Failed to get auth token")
      return
    }
    const response = await window.fetch("/skeem/generateLoginToken", {
      method: "POST",
      headers: {
        "X-Skeem-Auth-Token": skeemAuthToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: user.id }),
    })
    if (!response.ok) {
      alerts.genericError(await response.text())
      return
    }
    const json = await response.json()
    setLocalStorageWithExpiry(EOS_SKEEM_TOKEN, json.token ?? "", 1000 * 60 * 60 * 10)
    window.location.reload()
  })

  // Opens the customer's account to manage their favourites, without their password. The customer
  // area hides everything that writes apart from favourites - see utils/branchGuestSession.
  const handleGuestSignIn = useAsyncCallback(async (user: { id: string }) => {
    const skeemAuthToken = await skeem.getAuthToken()
    if (!skeemAuthToken) {
      alerts.genericError("Failed to get auth token")
      return
    }
    const response = await window.fetch("/skeem/generateLoginToken", {
      method: "POST",
      headers: {
        "X-Skeem-Auth-Token": skeemAuthToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: user.id, guest: true }),
    })
    if (!response.ok) {
      alerts.genericError(await response.text())
      return
    }
    const json = await response.json()
    startBranchGuestSession({
      customerName: customer?.name ?? "this customer",
      returnTo: window.location.pathname,
      startedAt: Date.now(),
      // Kept so leaving the guest view puts the staff member's own session back
      staffToken: window.localStorage.getItem(EOS_SKEEM_TOKEN),
    })
    // Shorter than a normal login-as session: this is meant for a quick visit
    setLocalStorageWithExpiry(EOS_SKEEM_TOKEN, json.token ?? "", 1000 * 60 * 60 * 2)
    window.location.assign(eRoutes["/favourites"])
  })

  const [confirmBlockNode, getConfirmBlockNode] = useBlockUserToggle()
  const [mergeContactNode, getConfirmationOfMerge] = useConfirmation<
    { defaultName: string; mergingWithId: string },
    { name: string; id: string } | false
  >(({ onResolve, data }) => (
    <Modal
      render={() => (
        <List>
          <TextGroupMedium
            heading="Merge contact"
            body={
              <React.Fragment>
                <P>
                  Select the contact to merge with ${data.defaultName}. If both contacts have a user
                  account, any user account with the selected contact will be removed, otherwise
                  they will be combined.
                </P>
                <P weight={600} color={brandColors.blaze}>
                  Warning: This cannot be undone and should only be done if you{"'"}re certain that
                  there are not two user accounts in use. Although the data is combined - the select
                  list below should be used to choose the secondary account as all permissions are
                  retained from the first account selected.
                </P>
              </React.Fragment>
            }
          />
          <Form2.Form onSubmit={onResolve} initialData={{ name: data.defaultName }}>
            <Form2.Text field="name" label="Merged contact name" />
            <Form2.WithField<string>
              field="id"
              render={field => (
                <ContactSelectList
                  onChange={field.setValue}
                  value={field.getValue()}
                  customerId={customer?.id}
                  skeemFilter={{ not: { matches: { id: data.mergingWithId } } }}
                />
              )}
            />
            <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
          </Form2.Form>
        </List>
      )}
      isOpen
    />
  ))

  useAsyncEffect(async () => {
    await fetchContacts()
  }, [id])

  async function fetchContacts() {
    const customer = await skeem.fetch2<ICustomer>({
      first: {
        name: "customers",
        attributes: [
          "name",
          "isGenericCustomer",
          {
            name: "contacts",
            attributes: [
              "job_title",
              "name",
              "first_name",
              "last_name",
              "name",
              "email",
              "phone",
              "sage_id",
              "receivesMonthlyPriceList",
              "default_delivery_note",
              "useForMsEmailEventFetch",
              "is_default",
              "marketingAccepted",
              {
                name: "user",
                attributes: [
                  "name",
                  "skeem_email",
                  "last_seen",
                  "isBlocked",
                  "customerIsPrimaryAccountHolder",
                  "phoneNumber",
                  "marketingAccepted",
                  "customerAreaQuotesOnly",
                  "customerIsPrimaryAccountHolder",
                ],
              },
              { name: "quotes", attributes: ["created_at"] },
              { name: "orders", attributes: ["created_at"] },
            ],
            sort: { by: "name", direction: "DESC" },
          },
        ],
        filter: { matches: { id } },
      },
    })
    setCustomer(customer)
  }

  async function handleCreateContact() {
    assert(customer)
    const customerRecord = await skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: customer.id } },
      },
    })
    try {
      await handleCreate(customerRecord)
      fetchContacts()
    } catch (e) {
      console.error(e)
    }
  }

  async function handleEditContact(
    contact: ICustomer["contacts"][0],
    opts?: { focusUserSection?: boolean },
  ) {
    assert(customer)
    const customerRecord = await skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: customer.id } },
      },
    })
    const contactData = await getContactInformation({ contact, customer: customerRecord, opts })
    if (contactData === false) {
      return
    } else {
      try {
        const { createUser, emailOverrideField, password, quickInvite, ...mutationData } =
          contactData
        if (quickInvite) {
          try {
            const result = await skeem.mutate<{ user: { email: string } }>({
              contacts: {
                update: {
                  id: contact.id,
                  useForMsEmailEventFetch: mutationData.useForMsEmailEventFetch,
                  marketingAccepted: mutationData.marketingAccepted,
                  job_title: mutationData.job_title,
                  user: {
                    create: {
                      first_name: mutationData.first_name,
                      last_name: mutationData.last_name,
                      name:
                        mutationData.first_name || mutationData.last_name
                          ? mutationData.first_name + " " + mutationData.last_name
                          : mutationData.name,
                      skeem_email: emailOverrideField ?? mutationData.email,
                      userAccountType: "external",
                      customer: { set: customer.id },
                      branch: { set: customerRecord.branch.id },
                      skeem_password: generatePassword(),
                      phoneNumber: mutationData.phone,
                      marketingAccepted: mutationData.marketingAccepted,
                    },
                  },
                },
              },
            })
            await skeem.generatePasswordResetToken({ email: result.user.email })
            alerts.genericSuccess("Invite sent")
          } catch (error) {
            if (error.message.includes("Error mutating skeem_email: must be unique")) {
              alerts.genericError("Cannot create user account, email already in use.")
            }
            throw error
          }
        } else {
          await skeem.mutate({
            contacts: {
              update: {
                id: contact.id,
                ...mutationData,
                name:
                  mutationData.first_name || mutationData.last_name
                    ? mutationData.first_name + " " + mutationData.last_name
                    : mutationData.name,
                user: createUser
                  ? {
                      create: {
                        first_name: mutationData.first_name,
                        last_name: mutationData.last_name,
                        name:
                          mutationData.first_name || mutationData.last_name
                            ? mutationData.first_name + " " + mutationData.last_name
                            : mutationData.name,
                        skeem_email: emailOverrideField ?? mutationData.email,
                        skeem_password: generatePassword(),
                        userAccountType: "external",
                        customer: { set: customer.id },
                        branch: { set: customerRecord.branch.id },
                        phoneNumber: mutationData.phone,
                        marketingAccepted: mutationData.marketingAccepted,
                      },
                    }
                  : { set: contactData.user?.id || null },
              },
            },
          })
          if (mutationData.default_delivery_note) {
            const contacts = await skeem.fetch2<IRecord<"contacts">[]>({
              model: {
                name: "contacts",
                filter: {
                  and: [
                    { not: { matches: { id: contact.id } } },
                    {
                      matches: {
                        customer: customer.id,
                        branch: currentUser.branch.id,
                        default_delivery_note: true,
                      },
                    },
                  ],
                },
              },
            })
            await skeem.mutate({
              contacts: contacts.map(v => ({
                update: { id: v.id, default_delivery_note: false },
              })),
            })
          }
        }
        await fetchContacts()
        alerts.genericSuccess("Contact updated successfully")
      } catch (e) {
        console.error(e)
        if (e.message.includes("must be unique")) {
          alerts.genericError("Failed to update contact, email already in use.")
        } else {
          alerts.genericError("Failed to update contact: " + e.message)
        }
      }
    }
  }

  async function handleChangeUserPassword(contact: ICustomer["contacts"][0]) {
    const passwordData = await getPassword({})
    if (passwordData === false) {
      return
    } else {
      try {
        await skeem.mutate({
          contacts: {
            update: {
              id: contact.id,
              user: {
                update: {
                  id: contact.user.id,
                  skeem_password: passwordData.password,
                },
              },
            },
          },
        })
        alerts.genericSuccess("Password updated successfully")
      } catch (e) {
        console.error(e)
        alerts.genericError("Failed to update password, please try again")
      }
    }
  }

  async function blockContact(contact: ICustomer["contacts"][0]) {
    const confirmBlock = await getConfirmBlockNode({ contact, block: !contact.user.isBlocked })
    if (!confirmBlock) {
      return
    } else {
      try {
        await skeem.mutate({
          contacts: {
            update: {
              id: contact.id,
              user: {
                update: { id: contact.user.id, isBlocked: !contact.user.isBlocked },
              },
            },
          },
        })
        alerts.genericSuccess(`Contact ${contact.user.isBlocked ? "unblocked" : "blocked"}`)
        fetchContacts()
      } catch (e) {
        console.error(e)
        alerts.genericError(
          `Failed to ${contact.user.isBlocked ? "unblock" : "block"} contact, please try again`,
        )
      }
    }
  }

  type IMergeCustomer = IRecord<"contacts", "quotes" | "orders"> & {
    user: IRecord<"skeem_users", "jobs" | "comments">
  }

  async function handleBeginMerge(id: string, name: string) {
    const mergedContactData = await getConfirmationOfMerge({ defaultName: name, mergingWithId: id })
    if (!mergedContactData) {
      return
    }

    try {
      const { contact1, contact2 } = await skeem.fetch2<{
        contact1: IMergeCustomer
        contact2: IMergeCustomer
      }>({
        obj: {
          contact1: {
            first: {
              name: "contacts",
              attributes: ["quotes", "orders", { name: "user", attributes: ["jobs", "comments"] }],
              filter: { matches: { id } },
            },
          },
          contact2: {
            first: {
              name: "contacts",
              attributes: ["quotes", "orders", { name: "user", attributes: ["jobs", "comments"] }],
              filter: { matches: { id: mergedContactData.id } },
            },
          },
        },
      })

      await skeem.mutate({
        quotes: contact2.quotes.map(quote => ({
          update: { id: quote.id, contact: { set: contact1.id } },
        })),
      })
      await skeem.mutate({
        orders: contact2.orders.map(orders => ({
          update: { id: orders.id, contact: { set: contact1.id } },
        })),
      })
      if (!contact1.user && contact2.user) {
        await skeem.mutate({
          skeem_users: { update: { id: contact2.user.id, contact: { set: contact1.id } } },
        })
      } else if (contact1.user && contact2.user) {
        await skeem.mutate({
          jobs: contact2.user.jobs.map(job => ({
            update: { id: job.id, user: { set: contact1.user.id } },
          })),
        })
        await skeem.mutate({
          comments: contact2.user.comments.map(comment => ({
            update: { id: comment.id, user: { set: contact1.user.id } },
          })),
        })
        await skeem.mutate({ skeem_users: { destroy: contact2.user.id } })
      }

      await skeem.mutate({ contacts: { destroy: contact2.id } })
      fetchContacts()
      alerts.genericSuccess("Contacts merged")
    } catch (e) {
      console.error(e)
      alerts.genericError("There has been an issue merging contacts - please contact support")
    }
  }

  async function handleToggleMonthlyPriceList(contactId: string, value: boolean) {
    await skeem.mutate({
      contacts: {
        update: {
          id: contactId,
          receivesMonthlyPriceList: value,
        },
      },
    })
    fetchContacts()
  }

  async function handleToggleDefaultContact(contactId: string, value: boolean) {
    if (value) {
      await skeem.mutate({
        contacts: {
          updateBy: {
            set: { is_default: false },
            filter: { matches: { customer: id, is_default: true } },
          },
        },
      })
    }
    await skeem.mutate({
      contacts: {
        update: { id: contactId, is_default: value },
      },
    })
    fetchContacts()
  }

  async function handleToggleDeliveryNoteEmail(contactId: string, value: boolean) {
    let contactsToScrapDefault: IRecord<"contacts">[] = []
    if (value) {
      contactsToScrapDefault = await skeem.fetch2<IRecord<"contacts">[]>({
        model: {
          name: "contacts",
          filter: { matches: { customer: id, default_delivery_note: true } },
        },
      })
    }
    await skeem.mutate({
      contacts: [
        ...contactsToScrapDefault.map(contact => ({
          update: { id: contact.id, default_delivery_note: false },
        })),
        { update: { id: contactId, default_delivery_note: value } },
      ],
    })
    fetchContacts()
  }

  async function handleToggleUseForMsResources(contactId: string, value: boolean) {
    await skeem.mutate({
      contacts: { update: { id: contactId, useForMsEmailEventFetch: value } },
    })
    fetchContacts()
  }

  async function handleToggleCustomerIsPrimaryAccountHolder(userId: string, _value: boolean) {
    assert(customer, "Customer must be defined")

    const userIdsToDowngrade = customer.contacts
      .map(contact => contact.user?.id)
      .filter(id => id)
      .filter(id => id !== userId)

    await skeem.mutate({
      skeem_users: { update: { id: userId, customerIsPrimaryAccountHolder: true } },
    })
    await skeem.mutate({
      skeem_users: userIdsToDowngrade.map(id => ({
        update: { id, customerIsPrimaryAccountHolder: false },
      })),
    })
    fetchContacts()
  }

  async function handleToggleCustomerAreaQuotesOnly(skeemUserId: string, value: boolean) {
    await skeem.mutate({
      skeem_users: { update: { id: skeemUserId, customerAreaQuotesOnly: value } },
    })
    fetchContacts()
  }
  async function handleTogglePrimaryUser(userId: string) {
    assert(customer, "Customer must be defined")

    const userIdsToDowngrade = customer.contacts
      .map(contact => contact.user?.id)
      .filter(id => id)
      .filter(id => id !== userId)

    await skeem.mutate({
      skeem_users: { update: { id: userId, customerIsPrimaryAccountHolder: true } },
    })
    await skeem.mutate({
      skeem_users: userIdsToDowngrade.map(id => ({
        update: { id, customerIsPrimaryAccountHolder: false },
      })),
    })
    fetchContacts()
  }

  async function handleToggleMarketingAccepted(contactId: string, value: boolean) {
    assert(customer, "Customer must be defined")

    await skeem.mutate({
      contacts: { update: { id: contactId, marketingAccepted: value } },
    })
    fetchContacts()
  }

  async function handleDeleteContact(id: string) {
    const shouldDelete = await confirmDeleteContact()
    if (shouldDelete) {
      await skeem.mutate({ contacts: { destroy: id } })
      fetchContacts()
      alerts.genericSuccess("Contact deleted")
    }
  }

  async function handleDeleteContactWithAccount(id: string) {
    const shouldDelete = await handleConfirmDeleteWithAccount()
    if (shouldDelete) {
      const contact = await skeem.fetch2<IRecord<"contacts", "user">>({
        first: { name: "contacts", attributes: ["user"], filter: { matches: { id } } },
      })
      if (!contact?.user) {
        alerts.genericError("Unable to find the associated user account for this contact.")
        return
      }
      await skeem.mutate({ skeem_users: { destroy: contact.user.id } })
      await skeem.mutate({ contacts: { destroy: id } })
      fetchContacts()
      alerts.genericSuccess("Contact deleted and account blocked")
    }
  }

  async function handleDeleteUserAccount(id: string) {
    const shouldDelete = await handleConfirmDelete()
    if (shouldDelete) {
      await skeem.mutate({ skeem_users: { destroy: id } })
      await fetchContacts()
      alerts.genericSuccess("User account deleted")
    }
  }

  if (!customer?.contacts) {
    return <Placeholder.Table />
  }

  return (
    <div>
      {deleteContactNode}
      {confirmDeleteWithAccountNode}
      {contactFormNode}
      {passwordNode}
      {confirmBlockNode}
      {mergeContactNode}
      {confirmDeleteUserNode}
      <Spacer />
      <List>
        <Grid columns={["auto", "max-content"]} gap={8}>
          <div>
            <TextGroupMedium heading="Contacts" />
            <Spacer size={6} />
            <div
              css={css`
                max-width: 400px;
              `}
            >
              <Fields.Search value={contactQuery} onChange={setContactQuery} />
            </div>
          </div>
          <div>
            <ButtonsWrapper>
              <CreateButton onClick={handleCreateContact}>New contact</CreateButton>
            </ButtonsWrapper>
          </div>
        </Grid>
        <Table.HorizontalOverflow minWidth={1300}>
          <Table
            data={customer.contacts.filter(
              contact =>
                contact.name.trim().toLowerCase().includes(contactQuery.trim()) ||
                contact.email.trim().toLowerCase().includes(contactQuery.trim()),
            )}
          >
            <Table.Column heading={<BaselineHeading>Name / Email</BaselineHeading>}>
              {row => {
                const contact = row as ICustomer["contacts"][0]
                const jobTitle = contact.job_title ? (
                  <div
                    css={css`
                      white-space: nowrap;
                    `}
                  >
                    <P color="#888" size="small" blockSpacing={0}>
                      {contact.job_title}
                    </P>
                  </div>
                ) : null
                const emailLine = contact.email ? (
                  <div
                    css={css`
                      font-size: 12px;
                      font-weight: 400;
                      color: ${themeColors.grey};
                    `}
                  >
                    {contact.email}
                  </div>
                ) : null
                if (contact.sage_id) {
                  return (
                    <QuickToolTip content="This contact was imported from Sage so cannot be edited here. To change any details, edit the record in Sage and Sync your data">
                      <span
                        css={css`
                          text-decoration: underline;
                          text-decoration-style: dashed;
                        `}
                      >
                        {jobTitle}
                        {contact.name}
                        <br />
                        <span
                          css={css`
                            font-size: 11px;
                            font-weight: 600;
                            color: ${themeColors.grey};
                          `}
                        >
                          Imported from Sage
                        </span>
                        {emailLine}
                      </span>
                    </QuickToolTip>
                  )
                } else {
                  return (
                    <div>
                      <span
                        onClick={() => handleEditContact(contact)}
                        css={css`
                          font-weight: 600;
                          color: ${brandColors.darkMint};
                          text-decoration: underline;
                          cursor: pointer;
                        `}
                      >
                        {jobTitle}
                        {contact.name} <Icon icon={icons.edit} />
                      </span>
                      {emailLine}
                    </div>
                  )
                }
              }}
            </Table.Column>
            <Table.Column heading={<AngledHeading>Default</AngledHeading>}>
              {row => (
                <ToggleSwitch
                  small
                  value={row.is_default}
                  onChange={value => handleToggleDefaultContact(row.id, value)}
                  disabled={!row.email}
                />
              )}
            </Table.Column>
            <Table.Column heading={<AngledHeading>Rec. price list</AngledHeading>}>
              {row => (
                <QuickToolTipWrapper
                  showToolTip={!!customer.isGenericCustomer && !row.receivesMonthlyPriceList}
                  content={"Generic customers cannot have a default price list contact."}
                >
                  <ToggleSwitch
                    small
                    value={row.receivesMonthlyPriceList}
                    onChange={value => handleToggleMonthlyPriceList(row.id, value)}
                    disabled={
                      !row.email || (customer.isGenericCustomer && !row.receivesMonthlyPriceList)
                    }
                  />
                </QuickToolTipWrapper>
              )}
            </Table.Column>
            <Table.Column heading={<AngledHeading>Default Del. Note Recpt.</AngledHeading>}>
              {row => (
                <ToggleSwitch
                  small
                  value={row.default_delivery_note}
                  onChange={value => handleToggleDeliveryNoteEmail(row.id, value)}
                  disabled={!row.email}
                />
              )}
            </Table.Column>
            <Table.Column heading={<AngledHeading>MS Email/Events</AngledHeading>}>
              {row => (
                <ToggleSwitch
                  small
                  value={row.useForMsEmailEventFetch}
                  onChange={value => handleToggleUseForMsResources(row.id, value)}
                  disabled={!row.email}
                />
              )}
            </Table.Column>
            <Table.Column
              heading={
                <QuickToolTip content="Primary users can set permissions for other users when accessing the customer app">
                  <AngledHeading>Primary User</AngledHeading>
                </QuickToolTip>
              }
            >
              {row =>
                row.user ? (
                  <ToggleSwitch
                    small
                    value={row.user.customerIsPrimaryAccountHolder}
                    onChange={value =>
                      handleToggleCustomerIsPrimaryAccountHolder(row.user.id, value)
                    }
                    disabled={!row.email}
                  />
                ) : (
                  ""
                )
              }
            </Table.Column>
            <Table.Column heading={<AngledHeading>Marketing Accepted</AngledHeading>}>
              {row =>
                row.user ? (
                  <ToggleSwitch
                    small
                    value={row.marketingAccepted}
                    onChange={value => handleToggleMarketingAccepted(row.id, value)}
                    disabled={!row.email}
                  />
                ) : (
                  ""
                )
              }
            </Table.Column>
            <Table.Column
              heading={
                <QuickToolTip content="When enabled, this user will only be able to create quotes in the customer area.">
                  <AngledHeading>Quotes Only</AngledHeading>
                </QuickToolTip>
              }
            >
              {row =>
                row.user ? (
                  <ToggleSwitch
                    small
                    value={row.user.customerAreaQuotesOnly}
                    onChange={value => handleToggleCustomerAreaQuotesOnly(row.user.id, value)}
                    disabled={!row.user}
                  />
                ) : (
                  ""
                )
              }
            </Table.Column>
            <Table.Column heading={<BaselineHeading>User account</BaselineHeading>}>
              {row => {
                const contact = row as ICustomer["contacts"][0]
                return contact.user ? (
                  <Grid columns={["max-content", "min-content"]} gap={4} alignItems="start">
                    <div>
                      {contact.user.customerIsPrimaryAccountHolder && (
                        <div
                          css={css`
                            margin-bottom: 2px;
                            background-color: ${themeColors.green};
                            padding: 2px;
                            color: white;
                            text-align: center;
                          `}
                        >
                          <b>Primary User</b>
                        </div>
                      )}
                      <b>{contact.user.skeem_email}</b>
                      <br />
                      Last activity:{" "}
                      {contact.user.last_seen
                        ? differenceInSeconds(new Date(), new Date(contact.user.last_seen)) < 30
                          ? "Just now"
                          : formatDistanceToNowStrict(new Date(contact.user.last_seen), {
                              addSuffix: true,
                            })
                        : "-"}
                      {contact.user.isBlocked ? (
                        <P weight={600} color="red" blockSpacing={0} size="small">
                          Contact blocked
                        </P>
                      ) : null}
                    </div>
                    <DropdownMenu
                      items={[
                        {
                          children: "View user",
                          to: buildRoute(eRoutes["/system/users/:id"], { id: contact.user.id }),
                        },
                        currentUser.canEditGlobalUserPermissions && {
                          children: "Login as user",
                          onClick: () => handleSignInAs(contact.user),
                        },
                        (currentUser.canGuestLoginToCustomerAccounts ||
                          currentUser.canEditGlobalUserPermissions) && {
                          children: "Guest login (favourites)",
                          onClick: () => handleGuestSignIn(contact.user),
                          disabled: handleGuestSignIn.running,
                        },
                        "separator",
                        {
                          children: contact.user.last_seen ? "Send Password Reset" : "Send invite",
                          onClick: () => {
                            skeem.generatePasswordResetToken({ email: contact.user.skeem_email })
                            alerts.genericSuccess(
                              contact.user.last_seen ? "Password Reset Sent" : "Invite sent",
                            )
                          },
                        },
                        {
                          children: "Change password",
                          onClick: () => handleChangeUserPassword(contact),
                        },
                        {
                          children: `Set as primary user`,
                          onClick: () => handleTogglePrimaryUser(contact.user.id),
                        },
                        {
                          children: `${contact.user.isBlocked ? "Unblock" : "Block"} account`,
                          onClick: () => blockContact(contact),
                        },
                        {
                          children: `Delete user account`,
                          onClick: () => handleDeleteUserAccount(contact.user.id),
                        },
                      ].filter(Boolean)}
                    >
                      <IconButton icon={icons.ellipsis} buttonStyle="text" />
                    </DropdownMenu>
                  </Grid>
                ) : row.email && row.name ? (
                  <Button
                    buttonColor="white"
                    onClick={() => handleEditContact(contact, { focusUserSection: true })}
                  >
                    Create / Assign user account
                  </Button>
                ) : (
                  <span>Cannot assign user to the contact</span>
                )
              }}
            </Table.Column>
            <Table.DataColumn
              field="phone"
              heading={<BaselineHeading>Phone</BaselineHeading>}
              render={phone => (phone ? <a href={["tel:", phone].join("")}>{phone}</a> : null)}
            />
            <Table.Column heading={<BaselineHeading># of quotes past 30 days</BaselineHeading>}>
              {row => {
                const contact = row as ICustomer["contacts"][0]
                return (
                  <Link
                    to={buildRoute(
                      eRoutes["/customers/:id/quotes"],
                      { id },
                      { qs: { contact: row.id } },
                    )}
                  >
                    {
                      contact.quotes.filter(
                        quote =>
                          quote.created_at &&
                          new Date(quote.created_at) > sub(new Date(), { days: 30 }),
                      ).length
                    }{" "}
                    (View quotes)
                  </Link>
                )
              }}
            </Table.Column>
            <Table.Column heading={<BaselineHeading># of orders past 30 days</BaselineHeading>}>
              {row => {
                const contact = row as ICustomer["contacts"][0]
                return (
                  <Link
                    to={buildRoute(
                      eRoutes["/customers/:id/orders"],
                      { id },
                      { qs: { contact: row.id } },
                    )}
                  >
                    {
                      contact.orders.filter(
                        order =>
                          order.created_at &&
                          new Date(order.created_at) > sub(new Date(), { days: 30 }),
                      ).length
                    }{" "}
                    (View orders)
                  </Link>
                )
              }}
            </Table.Column>
            <MenuColumn
              items={row => [
                { children: "Merge contact", onClick: () => handleBeginMerge(row.id, row.name) },
                {
                  children: "Delete contact",
                  onClick: row.sage_id
                    ? undefined
                    : row.user
                    ? () => handleDeleteContactWithAccount(row.id)
                    : () => handleDeleteContact(row.id),
                  disabled: row.sage_id,
                },
              ]}
            />
          </Table>
        </Table.HorizontalOverflow>
        <Spacer size={64} />
      </List>
    </div>
  )
}

export function useContactCreateForCustomerConfirmation() {
  const alerts = useSiteAlerts()
  const skeem = window.skeem

  const [node, confirm] = useConfirmation<
    {
      contact: Partial<ICustomer["contacts"][0]>
      customer: IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
      opts?: { focusUserSection?: boolean }
    },
    | (Omit<Partial<ICustomer["contacts"][0]>, "id"> & {
        createUser: boolean
        password?: string
        emailOverrideField?: string
        quickInvite?: boolean
      })
    | false
  >(({ onResolve, data }) => {
    const { quotes, orders, ...initialData } = data.contact
    const [enterManualEmail, setEnterManualEmail] = useState(false)

    return (
      <Modal
        render={() => (
          <Grid gap={12}>
            <TextGroupMedium
              heading={
                data.opts?.focusUserSection
                  ? "Assign / create user for contact"
                  : "Enter information for Contact"
              }
              body={`Contact for customer: ${data.customer?.name}`}
            />
            <Form2.Form initialData={initialData} onSubmit={onResolve}>
              <CheckEmailForNewUserNotTaken
                onIsTaken={() => setEnterManualEmail(true)}
                isAskingForOverride={enterManualEmail}
              />
              {!data.opts?.focusUserSection && (
                <React.Fragment>
                  <Form2.Text field="job_title" />
                  <Form2.Text field="first_name" validations={{ presence: true }} />
                  <Form2.Text field="last_name" validations={{ presence: true }} />
                  <Form2.Email field="email" validations={{ presence: true }} />
                  <Form2.Phone field="phone" />
                  <QuickToolTipWrapper
                    showToolTip={!!data.customer.isGenericCustomer}
                    content={"Generic customers cannot have a default price list contact."}
                  >
                    <Form2.Checkbox
                      field="receivesMonthlyPriceList"
                      label="Receives Price List When Sent"
                      disabled={!!data.customer.isGenericCustomer}
                    />
                  </QuickToolTipWrapper>
                </React.Fragment>
              )}
              <Form2.WithField<boolean>
                field="createUser"
                render={field =>
                  field.getValue() ? (
                    <Grid gap={8}>
                      {enterManualEmail && (
                        <FieldWithDescription description="This email is already in use for another user accounts. Contacts do not need unique email addresses but user accounts do. Enter a unique email address for the contact here.">
                          <Form2.Email
                            field="emailOverrideField"
                            label="User account email (Must be unique)"
                            validations={{ presence: true }}
                          />
                        </FieldWithDescription>
                      )}
                      <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="start" gap={8}>
                        <List>
                          <FloatingBox>
                            <H4>User</H4>
                            <P size="small">
                              On save, a password will be generated and a user account will be
                              created for this contact.
                            </P>
                            <Spacer />
                            <Form2.WithField<boolean>
                              field="quickInvite"
                              render={field => (
                                <Fields.Checkbox
                                  value={field.getValue()}
                                  onChange={field.setValue}
                                  label="Send invite when saving new account"
                                />
                              )}
                            />
                          </FloatingBox>
                        </List>
                        <div>
                          <CancelButton
                            onClick={() => field.setValue(false)}
                            extendTheme={{ padding: 8, fontSize: 12 }}
                          >
                            Choose existing
                          </CancelButton>
                        </div>
                      </Grid>
                    </Grid>
                  ) : (
                    <List>
                      <Form2.WithForm
                        subscribe={{ dataChange: true }}
                        render={form => (
                          <Form2.WithField<{ id: string }>
                            field="user"
                            render={userSelectField => (
                              <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="end" gap={8}>
                                <UserSelectList
                                  label="Select an existing user"
                                  value={userSelectField.getValue()?.id}
                                  onChange={(id, user) => {
                                    userSelectField.setValue({ id })

                                    form.getField("first_name").setValue(user?.first_name ?? "")
                                    form.getField("last_name").setValue(user?.last_name ?? "")
                                    form.getField("email").setValue(user?.skeem_email ?? "")
                                    form.getField("phone").setValue(user?.phoneNumber ?? "")
                                    form
                                      .getField("marketingAccepted")
                                      .setValue(user?.marketingAccepted ?? false)
                                  }}
                                  customerId={data.customer.id}
                                  branchId={data.customer.branch.id ?? undefined}
                                  filter={{
                                    or: [
                                      data.contact.id && {
                                        matches: {
                                          contact: data.contact.id,
                                        },
                                      },
                                      {
                                        matches: {
                                          contact: null,
                                        },
                                      },
                                    ].filter(Boolean),
                                  }}
                                />
                                <div>
                                  <CreateButton
                                    onClick={() => field.setValue(true)}
                                    extendTheme={{ padding: 8, fontSize: 12 }}
                                  >
                                    Create new user
                                  </CreateButton>
                                </div>
                              </Grid>
                            )}
                          />
                        )}
                      />
                    </List>
                  )
                }
              />
              <Form2.Checkbox
                field="default_delivery_note"
                label="Use this contact as the default contact for delivery notes"
                disabled={data.customer.isGenericCustomer}
              />
              <Form2.Checkbox
                field="useForMsEmailEventFetch"
                label="Use this contact in Emails & Events pulled from outlook"
              />
              <Form2.Checkbox field="marketingAccepted" />
              <Grid columns={["auto", "max-content"]}>
                <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
              </Grid>
            </Form2.Form>
          </Grid>
        )}
        isOpen
      />
    )
  })

  async function handleCreate(
    customer: IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> },
  ) {
    const contact = await confirm({ customer, contact: {} })
    if (contact === false) {
      return
    } else {
      try {
        const { createUser, emailOverrideField, password, quickInvite, ...mutationData } = contact
        const createdContact = await skeem.mutate<{
          default_delivery_note?: boolean
          email: string
          user: { email: string }
        }>({
          contacts: {
            create: {
              ...mutationData,
              name: mutationData.first_name + " " + mutationData.last_name,
              source_branch_id: customer.branch.id,
              branch: { set: customer.branch.id },
              customer: { set: customer.id },
              receivesMonthlyPriceList: !!mutationData.receivesMonthlyPriceList,
              user: createUser
                ? {
                    create: {
                      name: mutationData.first_name + " " + mutationData.last_name,
                      first_name: mutationData.first_name,
                      last_name: mutationData.last_name,
                      skeem_email: emailOverrideField ?? mutationData.email,
                      skeem_password: generatePassword(),
                      userAccountType: "external",
                      customer: { set: customer.id },
                      branch: { set: customer.branch.id },
                      phoneNumber: mutationData.phone,
                      marketingAccepted: mutationData.marketingAccepted,
                    },
                  }
                : { set: mutationData.user?.id || null },
            },
          },
        })
        if (mutationData.default_delivery_note) {
          const contacts = await skeem.fetch2<IRecord<"contacts">[]>({
            model: {
              name: "contacts",
              filter: {
                and: [
                  { not: { matches: { id: createdContact.id } } },
                  {
                    matches: {
                      customer: customer.id,
                      branch: customer.branch.id,
                      default_delivery_note: true,
                    },
                  },
                ],
              },
            },
          })
          await skeem.mutate({
            contacts: contacts.map(v => ({ update: { id: v.id, default_delivery_note: false } })),
          })
        }
        if (quickInvite && createUser) {
          await skeem.generatePasswordResetToken({ email: createdContact.user.email })
          alerts.genericSuccess("Invite sent")
        }
        alerts.genericSuccess("Contact created successfully")
        return createdContact
      } catch (e) {
        console.error(e)
        if (e.message.includes("must be unique")) {
          alerts.genericError("Failed to create contact, email already in use.")
        } else {
          alerts.genericError("Failed to create contact: " + e.message)
        }
      }
    }
  }

  return [node, confirm, handleCreate] as const
}

export function CheckEmailForNewUserNotTaken({
  onIsTaken,
  isAskingForOverride,
}: {
  onIsTaken(): void
  isAskingForOverride?: boolean
}) {
  const skeem = window.skeem
  useFormSubmitLock(async formState => {
    const emailField = formState.getField("email")
    const passwordField = formState.getField("password")
    const emailOverrideField = formState.getField("emailOverrideField")
    if (!passwordField.getValue()) {
      return true
    }
    const matchingEmailsCount = await skeem.fetch2<number>({
      count: {
        name: "skeem_users",
        filter: {
          matches: { skeem_email: emailOverrideField.getValue() ?? emailField.getValue() },
        },
      },
    })
    if (isBlank(emailOverrideField.getValue()) && isAskingForOverride) {
      emailOverrideField.setError(["Please enter an email address"])
      return false
    }
    if (matchingEmailsCount > 0) {
      emailOverrideField.setError(["Email is already taken"])
      onIsTaken()
      return false
    } else {
      return true
    }
  })
  return null
}

export function useEditUserConfirmation() {
  return useConfirmation<{}, { password: string } | false>(({ onResolve }) => (
    <Modal
      render={() => (
        <Form2.Form onSubmit={onResolve}>
          <Form2.Password field="password" label="Password" />
          <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
        </Form2.Form>
      )}
      isOpen
    />
  ))
}

function BaselineHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        height: 72px;
        display: flex;
        align-items: flex-end;
        padding-bottom: 4px;
      `}
    >
      {children}
    </div>
  )
}

function AngledHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        height: 72px;
        display: flex;
        align-items: flex-end;
        padding-bottom: 4px;
        overflow: visible;
        width: 24px;
      `}
    >
      <div
        css={css`
          transform: rotate(-45deg);
          transform-origin: bottom left;
          white-space: nowrap;
          font-size: 11px;
          margin-left: 10px;
        `}
      >
        {children}
      </div>
    </div>
  )
}

function generatePassword() {
  const alphabet = "abcdefghijklmnopqrstuvwxyz"
  return Array(12)
    .fill(["0123456789", alphabet, alphabet.toUpperCase()].join(""))
    .map(function (x) {
      return x[Math.floor(Math.random() * x.length)]
    })
    .join("")
}
